import React, { useState, useEffect, useMemo } from "react";
import moment from "moment";
import Geocode from "react-geocode";
import GoogleMapReact from "google-map-react";
import {
  getEvent,
  setCompanyFavouriteGRSDB,
  deleteCompanyFavouritesGRSDB,
  deleteCompanyFavourites,
} from "utils/api";
import buildIcalDownloadUrl from "utils/buildIcalDownloadUrl";
import Modal from "common/Modal";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Marker from "./Marker";
import PageNotFound from "PageNotFound";
import { useAsyncLoader } from "utils/useAsyncLoader";
import { Share, StarBorder, Star } from "@material-ui/icons";
import styled from "styled-components";
import { usePrepareData } from "utils/usePrepareData";

const HolderDiv = styled.div`
  width: 100%;
  height: 200px;
`;
const SubHeading = styled.p`
  font-weight: bold;
  color: ${({ theme }) => theme.black75};

  margin-bottom: 5px;
`;
const ColumnWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;
const Column = styled.div`
  width: 44%;
  padding: 10px 20px;
  & p {
    font-size: 14px;
    line-height: 22px;
    margin-top: 0;
    color: ${({ theme }) => theme.black75};
  }
`;
const Loading = styled.span`
  color: ${({ theme }) => theme.black75};
  position: absolute;
  z-index: 5;
  bottom: 30px;
  left: 15px;
  font-size: 32px;
  font-weight: bold;
`;
const UrlLink = styled.a`
  color: ${({ theme }) => theme.interactionColorDark};
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  margin-bottom: 15px;
  overflow-wrap: break-word;
  word-wrap: break-word;
  -ms-word-break: break-all;
  word-break: break-all;
`;
const Link = styled.a`
  display: flex;
  color: ${({ theme }) => theme.interactionColorDark};
  margin-bottom: 15px;
  background: transparent;
  text-decoration: none;
  align-items: center;
  width: 200px;
  font-weight: 600;
  > svg {
    color: ${({ theme }) => theme.black100};
  }
`;
const StyledShareIcon = styled(Share)`
  font-size: 1.1rem !important;
  margin-right: 2%;
`;
const Texts = styled.p`
  word-break: break-word;
`;
const FavoriteCompanyWrapper = styled.div`
  cursor: pointer;
  margin-left: 5px;
  display: flex;
  align-items: center;
  svg {
    margin-right: 5px;
  }
`;

async function formatCompanyData(company) {
  const { street_house_number, zip_code, city } = company.postal_address;
  const { latitude, longitude } = company.coordinates;
  const address = `${street_house_number}, ${zip_code} ${city}`;
  const response = await Geocode.fromAddress(address);
  const { place_id } = response.results[0];
  return {
    co_name: company.name,
    co_street: street_house_number,
    co_zip: zip_code,
    co_city: city,
    lat: latitude,
    lon: longitude,
    place_id,
  };
}

async function handleCompanyFavorite(eventData, isCompanyFavorite) {
  const { company } = eventData;
  if (company && isCompanyFavorite) {
    if (company.grsdb_id) {
      deleteCompanyFavouritesGRSDB(company.bisnode_id);
    } else if (company.id) {
      deleteCompanyFavourites(company.id);
    }
  } else {
    const res = await formatCompanyData(eventData);
    setCompanyFavouriteGRSDB(res);
  }
}

export default function EventDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams();
  const { isLoading, data: eventData, error } = useAsyncLoader(getEvent, eventId);

  const { channels } = usePrepareData();
  const [isCompanyFavorite, setIsComapnyFavorite] = useState(false);
  const markerColor = useMemo(() => {
    if (!eventData) {
      return null;
    }
    const ch = channels?.find(ch => ch.id === eventData.channel);
    return ch && ch.color;
  }, [eventData, channels]);

  useEffect(() => {
    if (eventData && eventData.company) {
      setIsComapnyFavorite(eventData.company.favorite);
    }
  }, [eventData]);

  const formatedDate = useMemo(() => eventData && moment(eventData.time).format("LLL"), [
    eventData,
  ]);

  const mapFormatedDate = useMemo(() => eventData && moment().format("YYYY-MM-DD"), [eventData]);

  const eventUrl = useMemo(
    () =>
      eventData &&
      formatedDate &&
      buildIcalDownloadUrl({
        name: eventData.name,
        description: `${eventData.text} ${eventData.description}`,
        location: `${eventData.postal_address.city}
      ${eventData.postal_address.street_house_number}
      ${eventData.postal_address.zip_code}`,
        dateStart: mapFormatedDate,
        dateEnd: "",
        url: eventData.origin_url,
      }),
    [eventData, formatedDate, mapFormatedDate]
  );

  const emailBody = useMemo(
    () =>
      eventData &&
      formatedDate &&
      `${eventData.name}
      ${eventData.text}
      ${eventData.description}
      ${eventData.abstract}
      Adresse
      ${eventData.postal_address.city} 
      ${eventData.postal_address.street_house_number}
      ${eventData.postal_address.zip_code}  
      ${eventData.origin_url}`,
    [eventData, formatedDate]
  );

  if (isLoading || !channels?.length) {
    return <Loading>Ereignisse werden geladen</Loading>;
  }

  return (
    <Modal
      isOpen={true}
      onRequestClose={() => navigate({ pathname: "/map", search: location.search })}
      noBottomMargin
      modalTitle={
        eventData ? (
          <div style={{ display: "flex" }}>
            {eventData.name}
            {eventData && eventData.bisnode_id && eventData.company && eventData.company.street && eventData.company.city && eventData.company.zip_code && (
              <FavoriteCompanyWrapper
                onClick={() => {
                  handleCompanyFavorite(eventData, isCompanyFavorite);
                  setIsComapnyFavorite(!isCompanyFavorite);
                }}
              >
                {isCompanyFavorite ? <Star /> : <StarBorder />}
              </FavoriteCompanyWrapper>
            )}
          </div>
        ) : (
          error && error.msg
        )
      }
    >
      {eventData ? (
        <>
          <HolderDiv>
            <GoogleMapReact
              options={{ fullscreenControl: false, zoomControl: false }}
              bootstrapURLKeys={{
                key: "AIzaSyDvXLFUSfLOZVCuK3_E3FwBn3lieP8Gt4U",
              }}
              defaultCenter={{
                lat: parseFloat(eventData.coordinates.latitude),
                lng: parseFloat(eventData.coordinates.longitude),
              }}
              defaultZoom={16}
              yesIWantToUseGoogleMapApiInternals
            >
              <Marker
                lat={parseFloat(eventData.coordinates.latitude)}
                lng={parseFloat(eventData.coordinates.longitude)}
                markerColor={markerColor}
              />
            </GoogleMapReact>
          </HolderDiv>
          <ColumnWrapper>
            <Column>
              <h3>{eventData.description}</h3>
              <SubHeading>Zusammenfassung</SubHeading>
              <Texts>{eventData.abstract}</Texts>
              <div>
                <SubHeading>Adresse</SubHeading>
                <Texts>
                  {eventData.postal_address.city} <br />
                  {eventData.postal_address.street_house_number} <br />
                  {eventData.postal_address.zip_code}
                </Texts>
              </div>
              <UrlLink url href={eventData.origin_url} target="_blank" title={eventData.origin_url}>
                {eventData.origin_url}
              </UrlLink>
              <Link
                style={{ marginTop: "15px" }}
                href={`mailto:?body=${encodeURIComponent(emailBody)}`}
              >
                <StyledShareIcon />
                Per E-Mail teilen
              </Link>
              <Link href={eventUrl}>
                <StyledShareIcon />
                Zum Kalender hinzufügen
              </Link>
            </Column>
            <Column>
              <h3>{formatedDate}</h3>
              <SubHeading>Beschreibung</SubHeading>
              <Texts style={{ whiteSpace: "pre-wrap" }}>{eventData.text}</Texts>
            </Column>
          </ColumnWrapper>
        </>
      ) : (
        <PageNotFound errorMsg={error && error.msg} />
      )}
    </Modal>
  );
}
